import { useEffect, useState } from "react";

import { Button, Input, Modal } from "@synthesis/shared-ui";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const CHANGE_EMAIL_TITLE = "Изменение адреса электронной почты";
const RESEND_COOLDOWN_SECONDS = 9 * 60 + 59;
const CONFIRMATION_CODE_MASK = { mask: "000-000" } as const;
const EMAILS_MISMATCH_MESSAGE = "Адреса электронной почты не совпадают";
const INVALID_EMAIL_MESSAGE = "Введён невалидный email";
const EMAILS_FIELD_ERROR = "invalid";

const isValidEmail = (value: string) => z.email().safeParse(value).success;

type ChangeEmailStep = "password" | "code" | "email" | "success";

type PasswordFormValues = {
  password: string;
};

type CodeFormValues = {
  confirmationCode: string;
};

type EmailFormValues = {
  newEmail: string;
  newEmailConfirm: string;
};

type ChangeEmailModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

const formatCountdown = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const ModalActions = ({
  continueLabel = "Продолжить",
  continueDisabled,
  onCancel,
}: {
  continueLabel?: string;
  continueDisabled?: boolean;
  onCancel: () => void;
}) => (
  <div className="flex flex-col gap-7">
    <Button type="submit" theme="primary" size="lg" isFullWidth disabled={continueDisabled}>
      {continueLabel}
    </Button>
    <div className="bg-neutrals-90 h-px w-full" aria-hidden />
    <Button type="button" theme="secondary" size="lg" isFullWidth onClick={onCancel}>
      Отменить
    </Button>
  </div>
);

const PasswordStep = ({ onContinue, onCancel }: { onContinue: () => void; onCancel: () => void }) => {
  const methods = useForm<PasswordFormValues>({
    defaultValues: { password: "" },
    mode: "onChange",
  });
  const password = useWatch({ control: methods.control, name: "password" }) ?? "";
  const canContinue = Boolean(password.trim());

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-7"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canContinue) {
            return;
          }

          onContinue();
        }}
      >
        <p className="paragraph-base text-type-minor">
          Для изменения адреса электронной почты введите текущие данные для входа в вашу учётную запись
        </p>

        <Input name="password" type="password" label="Пароль" required placeholder="************" size="lg" />

        <ModalActions continueDisabled={!canContinue} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

const CodeStep = ({ onContinue, onCancel }: { onContinue: () => void; onCancel: () => void }) => {
  const methods = useForm<CodeFormValues>({
    defaultValues: { confirmationCode: "" },
    mode: "onChange",
  });
  const confirmationCode = useWatch({ control: methods.control, name: "confirmationCode" }) ?? "";
  const [resendSecondsLeft, setResendSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (resendSecondsLeft === null || resendSecondsLeft <= 0) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setResendSecondsLeft((current) => (current === null || current <= 1 ? null : current - 1));
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [resendSecondsLeft]);

  const canContinue = confirmationCode.replace(/\D/g, "").length === 6;

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canContinue) {
            return;
          }

          onContinue();
        }}
      >
        <p className="paragraph-base text-type-minor">Введите код подтверждения, который был направлен на ваш адрес электронной почты</p>

        <div className="flex flex-col gap-7">
          <Input name="confirmationCode" label="Код подтверждения" required placeholder="***-***" size="lg" settings={CONFIRMATION_CODE_MASK} />

          {resendSecondsLeft === null ? (
            <p className="paragraph-sm text-type-common text-center">
              Код не пришёл?{" "}
              <button
                type="button"
                className="text-system-info-50 hover:text-system-info-70 font-medium"
                onClick={() => setResendSecondsLeft(RESEND_COOLDOWN_SECONDS)}
              >
                Отправить снова
              </button>
            </p>
          ) : (
            <p className="paragraph-sm text-type-light text-center">Можно запросить код снова через {formatCountdown(resendSecondsLeft)}</p>
          )}
        </div>

        <ModalActions continueDisabled={!canContinue} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

const EmailStep = ({ onContinue, onCancel }: { onContinue: () => void; onCancel: () => void }) => {
  const methods = useForm<EmailFormValues>({
    defaultValues: { newEmail: "", newEmailConfirm: "" },
    mode: "onChange",
  });
  const newEmail = useWatch({ control: methods.control, name: "newEmail" }) ?? "";
  const newEmailConfirm = useWatch({ control: methods.control, name: "newEmailConfirm" }) ?? "";
  const [submitError, setSubmitError] = useState<string | null>(null);

  const trimmedEmail = newEmail.trim();
  const trimmedConfirm = newEmailConfirm.trim();
  const bothFilled = Boolean(trimmedEmail && trimmedConfirm);

  useEffect(() => {
    setSubmitError(null);
    methods.clearErrors(["newEmail", "newEmailConfirm"]);
  }, [methods, newEmail, newEmailConfirm]);

  const setFieldsError = () => {
    methods.setError("newEmail", {
      type: "validate",
      message: EMAILS_FIELD_ERROR,
    });
    methods.setError("newEmailConfirm", {
      type: "validate",
      message: EMAILS_FIELD_ERROR,
    });
  };

  const handleContinue = () => {
    if (!bothFilled) {
      return;
    }

    if (!isValidEmail(trimmedEmail) || !isValidEmail(trimmedConfirm)) {
      setFieldsError();
      setSubmitError(INVALID_EMAIL_MESSAGE);
      return;
    }

    if (trimmedEmail !== trimmedConfirm) {
      setFieldsError();
      setSubmitError(EMAILS_MISMATCH_MESSAGE);
      return;
    }

    onContinue();
  };

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          handleContinue();
        }}
      >
        <p className="paragraph-base text-type-minor">
          Новый адрес электронной почты будет использоваться для входа в систему и получения уведомлений
        </p>

        <div className="flex flex-col gap-4">
          <Input name="newEmail" type="text" label="Новый адрес электронной почты" required placeholder="example@mail.com" size="lg" />
          <Input
            name="newEmailConfirm"
            type="text"
            label="Новый адрес электронной почты повторно"
            required
            placeholder="example@mail.com"
            size="lg"
          />
        </div>

        {submitError ? <p className="paragraph-sm text-system-error-50">{submitError}</p> : null}

        <ModalActions continueDisabled={!bothFilled} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

const SuccessStep = ({ onClose }: { onClose: () => void }) => (
  <div className="flex flex-col gap-6 pb-6">
    <p className="paragraph-base text-type-minor">На новый адрес электронной почты отправлено письмо со ссылкой для подтверждения изменения</p>

    <Button type="button" theme="secondary" size="lg" isFullWidth onClick={onClose}>
      Закрыть
    </Button>
  </div>
);

export const ChangeEmailModal = ({ isOpen, onOpenChange }: ChangeEmailModalProps) => {
  const [step, setStep] = useState<ChangeEmailStep>("password");

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setStep("password");
    }

    onOpenChange(open);
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };

  return (
    <Modal.Root isOpen={isOpen} setIsOpen={handleOpenChange}>
      <Modal.Content className="w-[min(456px,calc(100vw-2rem))]">
        <Modal.Title>{CHANGE_EMAIL_TITLE}</Modal.Title>
        <div className="px-6 py-8">
          <div className="heading-h3 text-type-common pb-[10px]">{CHANGE_EMAIL_TITLE}</div>

          {step === "password" ? <PasswordStep onContinue={() => setStep("code")} onCancel={handleCancel} /> : null}
          {step === "code" ? <CodeStep onContinue={() => setStep("email")} onCancel={handleCancel} /> : null}
          {step === "email" ? <EmailStep onContinue={() => setStep("success")} onCancel={handleCancel} /> : null}
          {step === "success" ? <SuccessStep onClose={handleCancel} /> : null}
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
